import { useState } from 'react'
import { t } from 'i18next'
import {
  SingleValue,
  SingleValueContainer,
  SingleValueSeparator,
  SingleValueType,
} from './components/SingleValue/SingleValue'
import { PaymentsSF } from './components/PaymentsSF/PaymentsSF'
import { RevenueByDevice } from './components/RevenueByDevice/RevenueByDevice'
import { ConversionGameHubChart } from './components/ConversionGameHubChart/ConversionGameHubChart'
import { RevenueMap } from './components/RevenueMap/RevenueMap'
import { SalesChart } from './components/SalesChart/SalesChart'
import { TopSkuItems } from '@/layouts/dashboard/components/TopCharts/TopSkuItems'
import { BestTriggers } from './components/BestTriggers/BestTriggers'
import { SankeyTopSku } from './components/SankeyTopSku/SankeyTopSku'
import { BalanceCard } from './components'
import { createPresets, getObjectParamsFromDateRange } from './util'
import { Page } from '@/ui'
import { useBanner } from '@/libs/hooks/useBanner'
import { TopClaimedSkuItems } from '@/layouts/dashboard/components/TopCharts/TopClaimedSkuItems'
import { TopCodes } from '@/layouts/dashboard/components/TopCharts/TopCodes'
import { HubVisitChart } from '@/layouts/dashboard/components/HubVisitChart/HubVisitChart'
import {
  CountryFlag,
  InputDateRange,
  InputDateRangePreset,
  Select,
  SelectFilter,
  SelectItem,
  cn,
  useCountryGroups,
  useIsMobile,
} from '@dashboard/ui'
import { useFeatureFlags, useGenerateCompanyPath } from '@/libs'
import { PaymentsChart } from '@/layouts/dashboard/components/PaymentsChart'
import { NewUsersChart } from '@/layouts/dashboard/components/NewUsersChart'
import { RevenueChart } from '@/layouts/dashboard/components/RevenueChart/RevenueChart'
import { DeviceType, FilterType, MeasureValue, OrderSourceType, UserSourceType } from '@/api/dashboard'
import { useTopMeasuresQuery } from '@/layouts/dashboard/api/useTopMeasuresQuery'
import { useHubVisitDataQuery } from '@/layouts/dashboard/api/useHubVisitDataQuery'
import { getCountries } from '@/translates'

let dashPresets: InputDateRangePreset[] | null = null

const createDashPresets = () => {
  if (!dashPresets) {
    dashPresets = createPresets()
  }
  return dashPresets
}

export default function Dashboard() {
  const { companyId, gameId } = useGenerateCompanyPath()
  const [presets] = useState(createDashPresets())
  const { featureFlags } = useFeatureFlags()
  const [selectedDateFilter, setSelectedDateFilter] = useState<InputDateRangePreset>(presets[4])
  const [demoSelectedFilter, setDemoSelectedFilter] = useState<'7' | '30'>('7')
  const [orderSource, setOrderSource] = useState<OrderSourceType | null>(null)
  const [deviceType, setDeviceType] = useState<DeviceType | null>(null)
  const [countries, setCountries] = useState<string[]>([])
  const isDemo = import.meta.env.VITE_DEMO_GAME_ID == gameId
  const countryGroups = useCountryGroups()

  const getComponentParams = () => {
    return {
      filters: {
        type: FilterType.Period,
        selectedFilter: selectedDateFilter,
        demoSelectedFilter: demoSelectedFilter,
        device_type: deviceType ? deviceType : undefined,
        order_source: orderSource ? orderSource : undefined,
        countries: countries?.length > 0 ? countries.join(',') : undefined,
      },
    }
  }

  const { data: topMeasures } = useTopMeasuresQuery(
    companyId,
    gameId,
    {
      ...getObjectParamsFromDateRange(selectedDateFilter.range),
      type: FilterType.Period,
      device_type: deviceType ? deviceType : undefined,
      order_source: orderSource ? orderSource : undefined,
      user_source: orderSource ? (orderSource as unknown as UserSourceType) : undefined,
      countries: countries?.length > 0 ? countries.join(',') : undefined,
    },
    demoSelectedFilter,
  )

  const { data: hubVisitData } = useHubVisitDataQuery(
    companyId,
    gameId,
    {
      ...getObjectParamsFromDateRange(selectedDateFilter.range),
      type: FilterType.Period,
      device_type: deviceType ? deviceType : undefined,
      countries: countries?.length > 0 ? countries.join(',') : undefined,
      user_source: orderSource ? (orderSource as unknown as UserSourceType) : undefined,
    },
    true,
    demoSelectedFilter,
  )

  const { getStickyTop } = useBanner()

  const isMobile = useIsMobile()

  let dauText = t('dashboard.dau')
  let dauToolTip = t('dashboard.dau.tooltip')

  if (selectedDateFilter.isHourly) {
    dauToolTip = t('dashboard.dau.hourly.tooltip')
  }

  const width = '100%'
  const height = '460px'

  const renderCharts = () => {
    if (isMobile) {
      return (
        <section className="flex flex-col gap-3">
          <SankeyTopSku style={{ width: '100%', flexGrow: 3 }} {...getComponentParams()} />
          <BestTriggers style={{ width: '100%', height: height, flexGrow: 2 }} {...getComponentParams()} />
          <HubVisitChart style={{ width: '100%', height: height }} {...getComponentParams()} />
          <NewUsersChart style={{ width: '100%', height: height }} {...getComponentParams()} />
          <PaymentsChart style={{ width: '100%', height: height }} {...getComponentParams()} />
          <PaymentsSF style={{ width: '100%', height: height }} {...getComponentParams()} />
          <ConversionGameHubChart style={{ width: '100%', height: height }} {...getComponentParams()} />
          <RevenueByDevice style={{ width: '100%', height: height }} {...getComponentParams()} />
          <TopSkuItems style={{ width: '100%', height: height, overflow: 'hidden' }} {...getComponentParams()} />
          <TopClaimedSkuItems style={{ width: '100%', height: height, overflow: 'hidden' }} {...getComponentParams()} />
          <TopCodes style={{ width: '100%', height: height, overflow: 'hidden' }} {...getComponentParams()} />
        </section>
      )
    }

    return (
      <>
        <div className="flex gap-5">
          <SankeyTopSku style={{ flexGrow: 3, height: height }} {...getComponentParams()} />
          <BestTriggers style={{ width: '453px', height: height }} {...getComponentParams()} />
        </div>
        <div className="grid w-full gap-5 [grid-template-columns:repeat(auto-fit,minmax(400px,1fr))]">
          <HubVisitChart style={{ width: width, height: height }} {...getComponentParams()} />
          <NewUsersChart style={{ width: width, height: height }} {...getComponentParams()} />
          <PaymentsChart style={{ width: width, height: height }} {...getComponentParams()} />
          <PaymentsSF style={{ width: width, height: height }} {...getComponentParams()} />
          <ConversionGameHubChart style={{ width: width, height: height }} {...getComponentParams()} />
          <RevenueByDevice style={{ width: width, height: height }} {...getComponentParams()} />
          <TopSkuItems style={{ width: width, height: height }} {...getComponentParams()} />
          <TopClaimedSkuItems style={{ width: width, height: height }} {...getComponentParams()} />
          <TopCodes style={{ width: width, height: height }} {...getComponentParams()} />
        </div>
      </>
    )
  }

  const renderExtraFilters = () => {
    if (!featureFlags.source_and_device_filter_dashboard_enabled) {
      return null
    }

    const countryItems: SelectItem[] = Object.entries(getCountries(true)).map(([value, children]) => ({
      children: children as string,
      icon: () => <CountryFlag country={value} />,
      value: value as string,
    }))

    return (
      <>
        <SelectFilter
          multiple={true}
          items={countryItems}
          groups={countryGroups}
          title={t('transactions-table.filter.countries')}
          value={countries}
          onChange={v => setCountries(v as string[])}
        >
          {t('transactions-table.filter.countries')}
        </SelectFilter>
        <SelectFilter
          multiple={false}
          items={[
            { children: t('dashboard.source.hub'), value: OrderSourceType.Hub },
            { children: t('dashboard.source.s2s'), value: OrderSourceType.S2S },
            { children: t('dashboard.source.checkout_link'), value: OrderSourceType.CheckoutLink },
            { children: t('dashboard.source.sdk'), value: OrderSourceType.Sdk },
          ]}
          value={orderSource}
          onChange={v => setOrderSource(v as OrderSourceType)}
        >
          {t('dashboard.sales-channel')}
        </SelectFilter>
        <SelectFilter
          multiple={false}
          items={[
            { children: t('dashboard.device-type.Apple'), value: DeviceType.Apple },
            { children: t('dashboard.device-type.Android'), value: DeviceType.Android },
            { children: t('dashboard.device-type.Other'), value: DeviceType.Other },
          ]}
          value={deviceType}
          onChange={v => setDeviceType(v as DeviceType)}
        >
          {t('dashboard.device-type')}
        </SelectFilter>
      </>
    )
  }

  const renderFilters = () => {
    if (isDemo) {
      return (
        <div>
          <Select
            items={[
              { children: t('filter.7days'), value: '7' },
              { children: t('filter.30days'), value: '30' },
            ]}
            value={demoSelectedFilter}
            onChange={v => setDemoSelectedFilter(v as '7' | '30')}
          />
        </div>
      )
    }

    if (isMobile) {
      return (
        <div className="flex items-center gap-2">
          {renderExtraFilters()}
          <Select
            items={presets.map((preset, index: number) => {
              return { children: preset.label, value: index }
            })}
            value={presets.findIndex(item => {
              return item.label === selectedDateFilter.label
            })}
            onChange={v => setSelectedDateFilter(presets[v as number])}
          />
        </div>
      )
    }

    return (
      <div className="flex items-center gap-2">
        {renderExtraFilters()}
        <InputDateRange
          value={selectedDateFilter.range}
          onChange={v => {
            setSelectedDateFilter(
              presets.find(it => it.range == v) || {
                range: v!,
                label: '',
              },
            )
          }}
          presets={presets}
        />
      </div>
    )
  }

  return (
    <>
      <Page className="gap-5 bg-fg-primary">
        <div className={cn('flex gap-5', isMobile ? 'flex-col' : '')}>
          <BalanceCard />
          <div className="grow overflow-x-hidden">
            <RevenueChart {...getComponentParams()} />
          </div>
        </div>

        <div
          className={cn(
            'sticky z-[2] flex gap-3 bg-fg-primary',
            !isMobile ? getStickyTop() : '',
            isMobile ? 'flex-col' : 'h-[72px] items-center justify-between',
          )}
        >
          <p className="font-nohemi text-[20px] font-medium">{t('dashboard.title')}</p>
          {renderFilters()}
        </div>

        <SingleValueContainer datatestid="dashboard/singlevalues">
          <SingleValue
            datatestid="dashboard/revenue"
            width="20%"
            value={
              topMeasures?.total_usd_revenue && {
                value: topMeasures.total_usd_revenue?.value
                  ? Math.max(topMeasures.total_usd_revenue.value, 0)
                  : topMeasures.total_usd_revenue?.value,
                prev_value: topMeasures.total_usd_revenue?.prev_value
                  ? Math.max(topMeasures.total_usd_revenue.prev_value, 0)
                  : topMeasures.total_usd_revenue?.prev_value,
              }
            }
            label={t('dashboard.revenue')}
            tooltip={t('dashboard.revenue.tooltip')}
            type={SingleValueType.money}
          />

          {!isMobile && (
            <>
              <SingleValueSeparator />
              <SingleValue
                datatestid="dashboard/dau"
                width="20%"
                value={hubVisitData?.dau as MeasureValue}
                label={dauText}
                tooltip={dauToolTip}
              />
              <SingleValueSeparator />
              <SingleValue
                datatestid="dashboard/mau"
                width="20%"
                value={topMeasures?.mau}
                label={t('dashboard.mau')}
                tooltip={t('dashboard.mau.tooltip')}
              />

              <SingleValueSeparator />
              <SingleValue
                datatestid="dashboard/arppu"
                width="20%"
                value={topMeasures?.arppu}
                label={t('dashboard.arppu')}
                type={SingleValueType.money}
                tooltip={t('dashboard.arppu.tooltip')}
              />
              <SingleValueSeparator />
              <SingleValue
                datatestid="dashboard/conversion"
                width="20%"
                value={topMeasures?.conversion}
                label={t('dashboard.conversion')}
                tooltip={t('dashboard.conversion.tooltip')}
                type={SingleValueType.percent}
              />
            </>
          )}
        </SingleValueContainer>

        {isMobile && (
          <>
            <SingleValueContainer datatestid="dashboard/mobileview/singlevalues/block1">
              <SingleValue
                datatestid="dashboard/mobileview/mau"
                width="20%"
                value={topMeasures?.mau}
                label={t('dashboard.mau')}
                tooltip={t('dashboard.mau.tooltip')}
              />

              <SingleValueSeparator />
              <SingleValue
                datatestid="dashboard/mobileview/dau"
                width="20%"
                value={hubVisitData?.dau as MeasureValue}
                label={dauText}
                tooltip={dauToolTip}
              />
            </SingleValueContainer>
            <SingleValueContainer datatestid="dashboard/mobileview/singlevalues/block2">
              <SingleValue
                datatestid="dashboard/mobileview/arppu"
                width="20%"
                value={topMeasures?.arppu}
                label={t('dashboard.arppu')}
                type={SingleValueType.money}
                tooltip={t('dashboard.arppu.tooltip')}
              />
              <SingleValueSeparator />
              <SingleValue
                datatestid="dashboard/mobileview/conversion"
                width="20%"
                value={topMeasures?.conversion}
                label={t('dashboard.conversion')}
                tooltip={t('dashboard.conversion.tooltip')}
                type={SingleValueType.percent}
              />
            </SingleValueContainer>
          </>
        )}

        <SalesChart {...getComponentParams()} />

        {renderCharts()}

        {!isMobile && (
          <div className="w-full">
            <RevenueMap {...getComponentParams()} />
            <div style={{ height: '100px' }} />
          </div>
        )}
      </Page>
    </>
  )
}
